import type { FunctionComponent, MouseEvent } from 'react';
import { useCallback } from 'react';

import { Logger } from '../../logger/Logger';
import { CoreLobbyMessageControllerV2, hostInfoMessage } from '../../messages/CoreLobbyMessageControllerV2';
import { useFeature } from '../../services/features/reactBindings/hooks';
import { L10nText } from '../../services/uiTranslations/reactBindings/L10nText';
import { useCoreDispatch } from '../../store/CoreStore';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { gameStateActions } from '../../store/slices/game/slice';
import { useHeaderSlice } from '../../store/slices/header/selectors';
import { headerActions } from '../../store/slices/header/slice';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { settingsActions } from '../../store/slices/settings/slice';
import { IconButton, Icons } from '../controls/iconButton/IconButton';
import { TrafficLightConnected } from '../game/trafficLight/TrafficLightConnected';
import { useVideoRef } from '../video_v8/VideoRefContext';

import { BackToGame } from './BackToGame';

export const BackToGameConnected: FunctionComponent = () => {
  const dispatch = useCoreDispatch();
  const { videoRef } = useVideoRef();
  const masterVolumeValue = useSettingsSlice.getMasterChannelValue();

  const tableName = useConfigSlice.tableName();
  const isLobbyOpen = useHeaderSlice.getIsMiniLobbyOpen();
  const isGameSuspended = useGameStateSlice.getIsGameSuspended();
  const lobbyVersion = useConfigSlice.casinoConfig()?.lobbyVersion;
  const poster = useGameStateSlice.getVideoPoster();
  const isBackToGameHidden = useHeaderSlice.getIsBackToGameHidden();

  const isBackToGameVideoEnabled: boolean = useFeature('isBackToGameVideoEnabled');

  const handleClick = useCallback((): void => {
    Logger.logEvent('backToGameClick');
    dispatch(headerActions.setNavRoute([]));
    dispatch(headerActions.setMiniLobbyOpen(false));
  }, [dispatch]);

  const handleCloseClick = useCallback((): void => {
    Logger.logEvent('backToGameClose');
    CoreLobbyMessageControllerV2.post(hostInfoMessage({ id: null }));
    dispatch(gameStateActions.setGameSuspended(true));
  }, [dispatch]);

  const handleSoundToggle = useCallback(
    (event?: MouseEvent<HTMLButtonElement>): void => {
      event?.stopPropagation();
      dispatch(settingsActions.toggleMasterVolume());
    },
    [dispatch]
  );

  if (lobbyVersion === '1') return;
  return (
    <BackToGame
      actionLabel={<L10nText tk="LOBBY_BACK_TO_GAME" />}
      isHidden={isBackToGameHidden}
      isOpen={isLobbyOpen && !isGameSuspended}
      name={tableName ? <L10nText tk={tableName} /> : undefined}
      onClick={handleClick}
      onClose={handleCloseClick}
      poster={poster}
      trafficLight={<TrafficLightConnected line />}
      videoElement={isBackToGameVideoEnabled ? videoRef?.current : undefined}
      secondaryActions={
        <IconButton icon={masterVolumeValue ? Icons.SoundOn : Icons.SoundOff} small ghost onClick={handleSoundToggle} />
      }
      nowPlayingLabel={<L10nText tk="LOBBY_NOW_PLAYING" />}
    />
  );
};
